import React, { useState, useEffect } from 'react';
import { 
  X, Save, Search, User, BookOpen, 
  Calculator, CheckCircle, AlertCircle, 
  Upload, Download, ChevronDown, 
  Layout, Edit3, Grid, ArrowLeft,
  GraduationCap, ChevronRight, LayoutDashboard,
  ClipboardList
} from 'lucide-react';
import EXAM_API from '../../api/services/examApi';
import resultApi from '../../api/services/resultApi';
import toast from 'react-hot-toast';
import SettingsHeader from '../../ui/SettingsHeader';
import Loader from '../../ui/Loader';

const ResultsEntryForm = ({ exam, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    examId: exam?._id || exam?.id || '',
    studentId: '',
    results: []
  });
  
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkResults, setBulkResults] = useState([]);
  const [selectedSubjectId, setSelectedSubjectId] = useState('');

  useEffect(() => {
    if (exam?._id || exam?.id) {
      loadStudents();
      initializeResults();
    }
  }, [exam]);

  const loadStudents = async () => {
    try {
      const response = await EXAM_API.getStudentsForExam(exam._id || exam.id);
      setStudents(response.data || []);
    } catch (error) {
    }
  };

  const initializeResults = () => {
    if (exam?.subjects) {
      const initialResults = exam.subjects.map(subject => ({
        subjectId: subject.subjectId || subject.id,
        subjectName: subject.subjectName,
        totalMarks: subject.totalMarks,
        passingMarks: subject.passingMarks,
        obtainedMarks: '',
        percentage: 0,
        grade: '',
        status: 'pending',
        markComponents: (subject.markComponents || []).map(c => ({
          name: c.name, maxMarks: c.maxMarks, minMarks: c.minMarks, obtainedMarks: ''
        }))
      }));
      setFormData(prev => ({ ...prev, results: initialResults }));
    }
  };

  const calculateGrade = (percentage) => {
    if (exam?.gradingSystem?.length > 0) {
      const sorted = [...exam.gradingSystem].sort((a, b) => b.minPercentage - a.minPercentage);
      const g = sorted.find(x => percentage >= x.minPercentage);
      return g ? g.grade : 'F';
    }
    return percentage >= 33 ? 'P' : 'F';
  };

  const handleResultChange = (index, field, value) => {
    const updated = [...formData.results];
    const result = updated[index];
    
    if (field === 'obtainedMarks') {
      const marks = parseFloat(value) || 0;
      const percentage = result.totalMarks > 0 ? (marks / result.totalMarks) * 100 : 0;
      updated[index] = {
        ...result,
        obtainedMarks: value,
        percentage: Math.round(percentage * 100) / 100,
        grade: calculateGrade(percentage),
        status: marks >= result.passingMarks ? 'pass' : 'fail'
      };
    }
    setFormData(prev => ({ ...prev, results: updated }));
  };

  const handleSubmit = async (e) => {
    if(e) e.preventDefault();
    setLoading(true);
    try {
      const payload = formData.results.map(r => ({
        studentId: selectedStudent._id || selectedStudent.id,
        registrationId: selectedStudent.registrationId,
        subjectId: r.subjectId,
        obtainedMarks: parseFloat(r.obtainedMarks) || 0,
        grade: r.grade,
        examId: exam._id || exam.id
      }));
      await EXAM_API.createBulkResults(payload);
      toast.success("Score records updated");
      onSubmit();
    } catch (error) {
      toast.error('Sync failed');
    } finally { setLoading(false); }
  };

  const handleBulkEntry = () => {
    setBulkMode(true);
    const bulkData = students.map(student => ({
      ...student,
      results: exam.subjects.map(s => ({
        subjectId: s.subjectId || s.id,
        subjectName: s.subjectName,
        totalMarks: s.totalMarks,
        obtainedMarks: '',
        grade: '',
      }))
    }));
    setBulkResults(bulkData);
    if (exam.subjects?.length > 0) setSelectedSubjectId(exam.subjects[0].subjectId || exam.subjects[0].id);
  };

  const handleBulkResultChange = (studentIdx, subId, field, value) => {
    const updated = [...bulkResults];
    const student = updated[studentIdx];
    const resIdx = student.results.findIndex(r => r.subjectId === subId);
    if (resIdx === -1) return;
    
    if (field === 'obtainedMarks') {
      student.results[resIdx].obtainedMarks = value;
      const perc = (parseFloat(value) || 0) / student.results[resIdx].totalMarks * 100;
      student.results[resIdx].grade = calculateGrade(perc);
    }
    setBulkResults(updated);
  };

  const handleBulkSubmit = async () => {
    setLoading(true);
    try {
      const all = [];
      bulkResults.forEach(student => {
        student.results.forEach(r => {
          if (r.obtainedMarks !== '') {
            all.push({
              studentId: student._id || student.id,
              registrationId: student.registrationId,
              subjectId: r.subjectId,
              obtainedMarks: parseFloat(r.obtainedMarks),
              grade: r.grade,
              examId: exam._id || exam.id
            });
          }
        });
      });
      await EXAM_API.createBulkResults(all);
      toast.success("Bulk records synchronized");
      onSubmit();
    } catch (e) { toast.error("Sync failed"); }
    finally { setLoading(false); }
  };

  return (
    <div className="mx-auto max-w-6xl py-10 px-4">
      <SettingsHeader
        title="Examination Scoring"
        description={`${exam.examName} • Session ${exam.academicYear}`}
        showBack={true}
        onBack={onClose}
        icon={ClipboardList}
        buttons={[
          { label: "Download Template", icon: Download, onClick: () => {}, variant: "secondary" },
          { label: bulkMode ? "Commit All Records" : "Save Student Marks", icon: Save, onClick: bulkMode ? handleBulkSubmit : handleSubmit, variant: "default", disabled: loading || (!bulkMode && !selectedStudent) }
        ]}
      />

      <div className="mt-10 bg-white rounded-[2.5rem] shadow-sm border border-gray-100 overflow-hidden flex flex-col">
        {/* Toggle Bar */}
        <div className="p-6 bg-gray-50/50 border-b border-gray-100 flex items-center justify-between">
          <div className="flex bg-white p-1 rounded-[1.2rem] border border-gray-100 shadow-sm">
             <button onClick={() => setBulkMode(false)} className={`px-6 py-2.5 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all ${!bulkMode ? 'bg-gray-900 text-white shadow-lg' : 'text-gray-400 hover:bg-gray-50'}`}>Individual Entry</button>
             <button onClick={handleBulkEntry} className={`px-6 py-2.5 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all ${bulkMode ? 'bg-gray-900 text-white shadow-lg' : 'text-gray-400 hover:bg-gray-50'}`}>Batch Entry (Bulk)</button>
          </div>
          <button className="flex items-center gap-2 px-5 py-3 bg-white text-[10px] font-black text-gray-700 border border-gray-100 rounded-xl hover:bg-gray-50 transition-all uppercase tracking-widest">
            <Upload size={14} className="text-emerald-500" />
            Import CSV File
          </button>
        </div>

        {!bulkMode ? (
          <div className="grid grid-cols-1 lg:grid-cols-12 min-h-[600px]">
             {/* Left: Student Browser */}
             <div className="lg:col-span-3 border-r border-gray-100 bg-gray-50/20">
                <div className="p-5 border-b border-gray-100 bg-white">
                   <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input 
                        placeholder="Search student..." 
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 text-xs bg-gray-50 border border-transparent rounded-xl outline-none focus:bg-white focus:border-primary-100 transition-all"
                      />
                   </div>
                </div>
                <div className="overflow-y-auto max-h-[600px] p-2 space-y-1 no-scrollbar">
                   {students.filter(s => s.name.toLowerCase().includes(searchTerm.toLowerCase())).map(student => (
                     <button 
                       key={student._id || student.id}
                       onClick={() => {
                           setSelectedStudent(student);
                           setFormData(prev => ({ ...prev, studentId: student._id || student.id }));
                       }}
                       className={`w-full p-4 rounded-2xl text-left transition-all group ${selectedStudent?._id === (student._id || student.id) ? 'bg-gray-900 text-white shadow-xl' : 'hover:bg-white text-gray-700'}`}
                     >
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-xs font-black tracking-tight">{student.name}</p>
                                <p className={`text-[8px] uppercase font-black tracking-widest mt-0.5 ${selectedStudent?._id === (student._id || student.id) ? 'text-gray-400' : 'text-gray-400'}`}>ID: {student.registrationId}</p>
                            </div>
                            <ChevronRight size={14} className={selectedStudent?._id === (student._id || student.id) ? 'opacity-50' : 'text-gray-200 group-hover:translate-x-1 transition-transform'} />
                        </div>
                     </button>
                   ))}
                </div>
             </div>

             {/* Right: Scoring Form */}
             <div className="lg:col-span-9 p-10 bg-white">
                {selectedStudent ? (
                  <div className="max-w-3xl animate-in fade-in slide-in-from-right-4 duration-500">
                     <div className="flex items-center gap-5 border-b border-gray-50 pb-8 mb-10">
                        <div className="w-14 h-14 rounded-2xl bg-gray-900 flex items-center justify-center font-black text-white text-xl shadow-xl shadow-gray-200">
                           {selectedStudent.name.charAt(0)}
                        </div>
                        <div>
                           <h3 className="text-xl font-black text-gray-900 tracking-tight">{selectedStudent.name}</h3>
                           <p className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] mt-1">Class Evaluation • {selectedStudent.class}</p>
                        </div>
                     </div>
                     
                     <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {formData.results.map((r, i) => (
                           <div key={i} className="p-6 rounded-[2rem] border border-gray-100 bg-gray-50/50 hover:bg-white hover:border-blue-100 hover:shadow-xl hover:shadow-blue-900/5 transition-all duration-300 group">
                              <div className="flex items-center justify-between mb-4 pb-2 border-b border-gray-100/50">
                                  <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{r.subjectName}</h4>
                                  <span className="text-[9px] font-black text-gray-300 tracking-tighter">MAX: {r.totalMarks}</span>
                              </div>
                              <div className="flex items-end gap-5">
                                 <div className="flex-1 space-y-2">
                                    <label className="text-[9px] font-black text-gray-300 uppercase tracking-widest ml-1">Score Obtained</label>
                                    <input 
                                      type="number" 
                                      value={r.obtainedMarks} 
                                      onChange={e => handleResultChange(i, 'obtainedMarks', e.target.value)}
                                      onWheel={(e) => e.target.blur()} 
                                      className="w-full text-2xl font-black bg-white border border-gray-100 px-4 py-3 rounded-2xl outline-none focus:border-blue-500 shadow-sm"
                                      placeholder="0"
                                    />
                                    {errors[`result_${i}`] && <p className="text-[8px] text-red-500 font-bold uppercase ml-1">{errors[`result_${i}`]}</p>}
                                 </div>
                                 <div className="w-20 text-center bg-white border border-gray-100 rounded-2xl py-3 shadow-sm group-hover:bg-blue-50 group-hover:border-blue-50 transition-all">
                                    <p className="text-[8px] font-black text-gray-300 uppercase tracking-widest mb-1">Grade</p>
                                    <p className={`text-2xl font-black ${r.grade === 'F' ? 'text-red-500' : 'text-blue-600'}`}>{r.grade || '-'}</p>
                                 </div>
                              </div>
                           </div>
                        ))}
                     </div>
                  </div>
                ) : (
                  <div className="h-full flex flex-col items-center justify-center text-center py-20 opacity-30">
                     <GraduationCap size={100} className="text-gray-100 mb-6" />
                     <h3 className="text-lg font-black text-gray-900 uppercase tracking-widest">Awaiting Selection</h3>
                     <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest max-w-xs leading-relaxed mt-2">Pick a student from the sidebar to begin processing evaluation records</p>
                  </div>
                )}
             </div>
          </div>
        ) : (
          /* Bulk Mode Content */
          <div className="p-10 bg-white">
             {/* Table headers and rows for bulk mode could follow here, but for brevity keeping current structure */}
             <div className="flex items-center justify-between mb-8 pb-6 border-b border-gray-100">
                <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-2xl bg-gray-900 flex items-center justify-center text-white shadow-lg">
                        <Grid size={20} />
                    </div>
                    <div>
                        <h3 className="text-base font-black text-gray-900 tracking-tight">Bulk Subject Evaluation</h3>
                        <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">Entering marks for all students at once</p>
                    </div>
                </div>
                <div className="flex items-center gap-4 bg-gray-50 p-2 rounded-2xl border border-gray-100">
                   <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest ml-3">Active Subject</span>
                   <select 
                     value={selectedSubjectId} 
                     onChange={e => setSelectedSubjectId(e.target.value)}
                     className="bg-white border border-gray-200 text-xs font-bold px-5 py-2.5 rounded-xl outline-none focus:ring-1 focus:ring-gray-300 appearance-none min-w-[150px]"
                   >
                      {exam.subjects.map(s => <option key={s.subjectId || s.id} value={s.subjectId || s.id}>{s.subjectName}</option>)}
                   </select>
                </div>
             </div>
             
             <div className="overflow-hidden border border-gray-100 rounded-[2.5rem] shadow-sm">
                <table className="w-full text-left">
                   <thead>
                      <tr className="bg-gray-50 border-b border-gray-100">
                         <th className="px-10 py-6 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Student Identity</th>
                         <th className="px-10 py-6 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] text-center">Score Input</th>
                         <th className="px-10 py-6 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] text-center">Result Grade</th>
                      </tr>
                   </thead>
                   <tbody className="divide-y divide-gray-50">
                      {bulkResults.map((s, idx) => {
                         const r = s.results.find(res => res.subjectId === selectedSubjectId);
                         return (
                           <tr key={idx} className="hover:bg-blue-50/30 transition-colors group">
                              <td className="px-10 py-6">
                                 <p className="text-sm font-black text-gray-900 tracking-tight group-hover:text-primary-700 transition-colors">{s.name}</p>
                                 <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest mt-0.5">{s.registrationId}</p>
                              </td>
                              <td className="px-10 py-6 text-center">
                                 <div className="flex items-center justify-center gap-4">
                                    <input 
                                      type="number" 
                                      value={r?.obtainedMarks}
                                      onChange={e => handleBulkResultChange(idx, selectedSubjectId, 'obtainedMarks', e.target.value)}
                                      onWheel={(e) => e.target.blur()} 
                                      className="w-24 text-center text-lg font-black py-2.5 bg-white border border-gray-100 rounded-2xl outline-none focus:border-blue-500 shadow-sm"
                                      placeholder="0"
                                    />
                                    <span className="text-[10px] font-black text-gray-300 uppercase">/ {r?.totalMarks}</span>
                                 </div>
                              </td>
                              <td className="px-10 py-6 text-center">
                                 <span className={`inline-block px-5 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest ${r?.grade === 'F' ? 'bg-red-50 text-red-600 border border-red-100' : 'bg-emerald-50 text-emerald-600 border border-emerald-100'}`}>
                                    {r?.grade || '-'}
                                 </span>
                              </td>
                           </tr>
                         );
                      })}
                   </tbody>
                </table>
             </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResultsEntryForm;
